import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Progress } from '../../ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useWebRTCContext } from '../../WebRTC/WebRTCContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { MEDIA_TONE_CLASS, resolveHandoffPhaseCfg } from '../../../lib/handoffMediaPhase';
import { isMeaningfulResolution } from '../../../lib/solutionResolution';
import { acceptOperatorCallSession, mediaRoomContractFor } from '../../../lib/operatorCallAccept';
import {
  Phone,
  MessageSquare, 
  Mail, 
  Headphones,
  Mic,
  MicOff,
  PhoneCall,
  PhoneOff,
  Pause,
  Play,
  Users,
  FileText,
  Search,
  Clock,
  Star,
  Send,
  Paperclip,
  Volume2,
  VolumeX,
  Monitor,
  Zap,
  Target,
  Award
} from 'lucide-react';

import { _getToken } from '../Scheduling/SchedulingManager';
import { createCaptureApi } from '../../../lib/encounterCaptureApi';
import EncounterCaptureBar from '../../WebRTC/EncounterCaptureBar';

// M4 — the SAME transport the Scheduling appointment-detail control uses, bound to the SAME token
// helper. One readiness authority and one segment authority for the whole Business Web plane; see
// lib/encounterCaptureApi.js for why neither is defined here.
const captureApi = createCaptureApi(_getToken);

// AI-Voice human-handoff media/transcript panel. HONEST: the banner reflects the real WebRTC
// media ladder (never claims "Human Call Active" before the peer connection is actually connected
// AND carrying remote audio), and the transcript/summary are polled read-only from the Phase B
// backend contract (GET .../interactions/{id}/transcript). Self-contained so it can't perturb the
// main desktop's state; upload/capture happens inside useWebRTC, not here.
// Phase/tone tables + honest resolution live in ../../../lib/handoffMediaPhase (pure + unit-tested).

// Backend WS terminal-event contract for an AI human-handoff. Any of these for a ringing interaction
// stops the ring + clears the incoming card (de-duped by interaction_id+status). Fallback status maps
// the message type when the payload omits an explicit `status`.
const TERMINAL_HANDOFF_STATUS = {
  'operator-handoff-accepted':  'accepted',
  'operator-handoff-cancelled': 'cancelled',
  'operator-handoff-expired':   'expired',
  'operator-handoff-resolved':  'resolved',
};

const HandoffMediaPanel = ({ interactionId, businessId, mediaPhase, hasLiveAudio, makeAuthenticatedRequest, t }) => {
  const [transcript, setTranscript] = useState(null);
  const isActive = mediaPhase === 'human_call_active';
  const isEnded = mediaPhase === 'ended';
  // Honest phase resolution: connected but no REAL remote audio => "Connected — no live audio", NOT active.
  const phaseCfg = resolveHandoffPhaseCfg(mediaPhase, hasLiveAudio);

  useEffect(() => {
    // Poll the read-only transcript/summary while the human call is live or just ended (summary lands
    // on hang-up finalize). No polling in idle/joining states — nothing to show yet.
    if (!interactionId || !businessId || !(isActive || isEnded)) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await makeAuthenticatedRequest(
          `/api/v1/operator-workspace/${businessId}/interactions/${interactionId}/transcript`);
        if (!res?.ok) return;
        const data = await res.json();
        if (alive) setTranscript(data);
      } catch (_) { /* transient; keep last known */ }
    };
    load();
    const iv = setInterval(load, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [interactionId, businessId, isActive, isEnded, makeAuthenticatedRequest]);

  // Nothing to render until a handoff media session is in play.
  if (!phaseCfg && !transcript) return null;

  const segments = (transcript && transcript.transcript) || [];
  const summary = transcript && transcript.operator_summary;
  const preHandoff = transcript && transcript.pre_handoff_summary;

  return (
    <div className="mb-4 space-y-3">
      {phaseCfg && (
        <div className={`rounded-md border px-3 py-2 text-sm font-medium flex items-center gap-2 ${MEDIA_TONE_CLASS[phaseCfg.tone]}`}>
          {phaseCfg.tone === 'green'
            ? <PhoneCall className="w-4 h-4" />
            : phaseCfg.tone === 'red'
              ? <PhoneOff className="w-4 h-4" />
              : <Clock className="w-4 h-4" />}
          <span>{t(phaseCfg.key)}</span>
        </div>
      )}

      {preHandoff && (
        <div className="rounded-md border bg-blue-50 border-blue-200 text-blue-900 px-3 py-2 text-xs">
          <span className="font-semibold">{t('od.aiContext')}: </span>{preHandoff}
        </div>
      )}

      {segments.length > 0 && (
        <div className="rounded-md border bg-white">
          <div className="px-3 py-2 border-b text-xs font-semibold text-gray-600 flex items-center gap-1">
            <FileText className="w-3.5 h-3.5" /> {t('od.liveTranscript')}
          </div>
          <div className="max-h-56 overflow-y-auto px-3 py-2 space-y-2">
            {segments.map((s) => (
              <div key={s.segment_id} className="text-sm">
                <span className={`font-semibold ${s.speaker_role === 'operator' ? 'text-green-700' : 'text-blue-700'}`}>
                  {s.speaker_display_name || (s.speaker_role === 'operator' ? t('od.speakerAgent') : t('od.speakerCustomer'))}:
                </span>{' '}
                <span className="text-gray-800">{s.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary && (summary.summary || (summary.action_items && summary.action_items.length) || (summary.commitments && summary.commitments.length)) && (
        <div className="rounded-md border bg-white">
          <div className="px-3 py-2 border-b text-xs font-semibold text-gray-600 flex items-center gap-1">
            <Award className="w-3.5 h-3.5" /> {t('od.callSummary')}
          </div>
          <div className="px-3 py-2 space-y-2 text-sm">
            {summary.summary && <p className="text-gray-800">{summary.summary}</p>}
            {summary.action_items && summary.action_items.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-600">{t('od.actionItems')}</div>
                <ul className="list-disc list-inside text-gray-800">
                  {summary.action_items.map((a, i) => (
                    <li key={i}>
                      {a.description}
                      {a.owner_display_name ? ` — ${a.owner_display_name}` : ''}
                      {a.due_at ? ` (${a.due_at})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {summary.commitments && summary.commitments.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-600">{t('od.commitments')}</div>
                <ul className="list-disc list-inside text-gray-800">
                  {summary.commitments.map((c, i) => (
                    <li key={i}>{c.commitment}{c.expected_date ? ` (${c.expected_date})` : ''}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const UnifiedAgentDesktop = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { makeAuthenticatedRequest, currentUser } = useCRMAuth();
  const { currentBusiness, getBusinessStaff } = useBusiness();
  const {
    callStatus: webRTCCallStatus,
    currentCall: webRTCCurrentCall,
    endCall: webRTCEndCall,
    toggleMute: webRTCToggleMute,
    acceptCall: webRTCAcceptCall,
    startScreenShare,
    remoteStream,
    hasRemoteAudio,                   // P0-E: real live remote audio present (media-truth)
    setExternalOperatorCallHandler,   // ADDITIVE: in-app operator-call ring/cancel signaling
    setExternalHandoffHandler,        // ADDITIVE: AI human-handoff real-time alert (operator-handoff-*)
    getLiveConnectionId,      // socket-registered conn id: lets the server exclude the winner from its own cancel
    operatorCallRing,         // invite that rang while this workspace was unmounted
    claimOperatorCallRing,    // single-use handover, so it is seeded exactly once
    clearOperatorCallRinging, // stop the ring when this surface DECLINES (accept stops it via joinMediaRoom)
    joinMediaRoom,            // ADDITIVE (Phase C): join the shared AI-Voice handoff media room as answerer
    mediaPhase,               // ADDITIVE (Phase C): honest media ladder (media_joining→…→human_call_active|media_failed)
    activeCallId,             // M4: the active room id — `enc_<appointment_id>` for a consultation
    localStream,              // M4: the call's EXISTING mic. Capture consumes it; it opens none.
  } = useWebRTCContext();   // shared singleton — no second socket
  // Remote call audio element — attaching the WebRTC remote stream here is what
  // makes the operator hear the caller AND lets the Volume control actually work.
  const remoteAudioRef = useRef(null);
  const [activeChannel, setActiveChannel] = useState('voice');
  const [callStatus, setCallStatus] = useState('available');
  const [isMuted, setIsMuted] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [quickResponse, setQuickResponse] = useState('');

  // Real staff identity — resolved from auth context
  const [staffRecord, setStaffRecord] = useState(null);
  const businessId = currentBusiness?.brandId || '';

  // Real live data state
  const [queues, setQueues] = useState({ voice_queue: 0, chat_queue: 0, email_queue: 0, social_queue: 0 });
  // A KPI TARGET IS EITHER REAL OR "—". `target: kpi.target || {...}` falls back on the WHOLE object, so
  // a payload carrying `target: {}` is truthy, defeats the fallback and renders "Target: undefined" /
  // "undefined%". Same honesty rule the D16 FCR value below already follows; a hardcoded substitute would
  // state a goal the tenant never set.
  const kpiTarget = (value, suffix = '') => {
    if (typeof value === 'number' && Number.isFinite(value)) return `${value}${suffix}`;
    const str = typeof value === 'string' ? value.trim() : '';
    return str ? `${str}${suffix}` : '—';
  };

  const [personalKPIs, setPersonalKPIs] = useState({
    callsToday: 0, aht: '—', fcr: '—', csat: '—', ticketsResolved: 0,
    target: { aht: '5:00', fcr: 85, csat: 4.5 },
  });
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [kbSearch, setKbSearch] = useState('');
  const [activeTickets, setActiveTickets] = useState([]);

  // Slice 2: real operator-workspace (server-authoritative availability/break, channel counts, transfer targets)
  const [workspace, setWorkspace] = useState(null);
  // Terminal handoff ledger (interaction_id -> terminal status) from the backend WS contract
  // (operator-handoff-accepted|cancelled|expired|resolved). Once an interaction reaches a terminal
  // state it is removed from OUR incoming queue (stops the ring, clears the card, non-actionable) and
  // de-duped so a late incoming copy for the same interaction_id+status can't re-ring. Pruned once the
  // server stops listing that interaction (below), so it can never grow unbounded.
  const [terminatedHandoffs, setTerminatedHandoffs] = useState({});
  
  // Productivity Tools dialogs state
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [scheduleCallbackOpen, setScheduleCallbackOpen] = useState(false);
  const [generateReportOpen, setGenerateReportOpen] = useState(false);
  const [screenShareOpen, setScreenShareOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [volumeDialogOpen, setVolumeDialogOpen] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(75);
  const [transferTarget, setTransferTarget] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [controlBusy, setControlBusy] = useState(false);   // in-flight backend call-control action
  // Slice 4: Generate Report + Screen Share — backend-backed, honest state.
  const [reportNotes, setReportNotes] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [lastReport, setLastReport] = useState(null);       // backend-generated report record
  const [reportIncludes, setReportIncludes] = useState({ transcript: false, ticket: true, callback: true, resolution: true });
  const [screenShareReason, setScreenShareReason] = useState('');
  const [screenShareBusy, setScreenShareBusy] = useState(false);
  const [screenShareSession, setScreenShareSession] = useState(null);  // backend session (never faked)

  // Customer communication quick-tools state (Call-back / Message / Email the active customer).
  // Ported from the consolidated agent desktop; every action is backend-gated (real 2xx), no fakes.
  const [quickMessageOpen, setQuickMessageOpen] = useState(false);
  const [quickMessageText, setQuickMessageText] = useState('');
  const [uadEmailOpen, setUadEmailOpen] = useState(false);
  const [uadEmailTo, setUadEmailTo] = useState('');
  const [uadEmailData, setUadEmailData] = useState({ subject: '', body: '' });
  const [commBusy, setCommBusy] = useState(false);   // in-flight customer-comm action (call/msg/email)

  // Call Control dialog states
  const [saveResolveDialogOpen, setSaveResolveDialogOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  // Operator PRE-SUBMISSION Solution Bank surface (opt-in). The reusable resolution is authored/edited
  // here and submitted for MANAGER/ADMIN review through the ONE canonical create endpoint
  // (POST /api/v1/solutions), which enforces the resolution-quality gate + stamps status=pending_approval.
  const [submitToSolutionBank, setSubmitToSolutionBank] = useState(false);
  const [solutionTitle, setSolutionTitle] = useState('');
  const [solutionResolutionSteps, setSolutionResolutionSteps] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [escalateTo, setEscalateTo] = useState('manager');
  
  // Form states
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'technical',
    customer: ''
  });
  
  const [callbackData, setCallbackData] = useState({
    customer: '',
    phone: '',
    date: '',
    time: '',
    notes: ''
  });
  
  const [reportConfig, setReportConfig] = useState({
    type: 'performance',
    period: 'today',
    format: 'pdf'
  });

  // Derived channel overview — server-authoritative operator-workspace counts when present,
  // else fall back to the analytics queue snapshot. email/social carry an `unavailable` flag
  // (backend `sources`) so we render "—" rather than a fabricated 0-queue.
  const cc = workspace?.channel_counts;
  // Real active-interaction context (server-authoritative) → the interaction panel binds names, not
  // raw IDs, and a reason/priority pulled straight from the accepted handoff. `current` is null until
  // an interaction is actually accepted for that channel — no fabricated call info.
  const _aiCtx = workspace?.active_interaction || null;
  const _ctxFor = (ch) => (_aiCtx && (_aiCtx.channel === ch)
    ? {
        customer: _aiCtx.customer_display_name || _aiCtx.customer_name || 'Customer',
        phone: _aiCtx.customer_phone || '',
        issue: _aiCtx.transcript_summary || _aiCtx.escalation_reason || _aiCtx.reason || '',
        priority: _aiCtx.priority || 'normal',
        duration: '',
        history: [],
        interaction_id: _aiCtx.interaction_id,
        source: _aiCtx.source || '',
      }
    : null);
  // `active` (the pulsing red "live" dot) means a genuinely ACCEPTED interaction is in progress on this
  // channel — NOT merely queue>0. A waiting/queued/stale item must never paint the channel as live
  // (that was part of the false "Live Call" impression). The queue COUNT badge stays honest either way.
  const activeChannels = {
    voice: { active: !!_ctxFor('voice'), queue: cc?.voice ?? queues.voice_queue, current: _ctxFor('voice') },
    chat:  { active: !!_ctxFor('chat'),  queue: cc?.chat ?? queues.chat_queue, current: _ctxFor('chat') },
    email: { active: false, queue: cc?.email ?? queues.email_queue, unavailable: !!cc?.sources?.email },
    social:{ active: false, queue: cc?.social ?? 0, unavailable: !!cc?.sources?.social },
  };

  // Call-control source of truth is the BACKEND interaction, not local state. active_interaction is
  // the one the operator is on; incoming_queue[0] is the next answerable one. Media (audio) is
  // browser-side WebRTC — `hasMedia` gates mute/volume honestly.
  const activeInteraction = workspace?.active_interaction || null;
  // ACTIONABILITY CONTRACT: only an item THIS operator may actually accept (backend `accept_allowed`)
  // drives the ringing/Answer state — so a visible+enabled Answer always maps to a real accept. Items
  // that are assigned to another operator / expired / ineligible are surfaced honestly (incoming_locked)
  // but never as an enabled Answer, and never as "Ready for Next Interaction". (accept_allowed !== false
  // keeps back-compat if an older backend omits the flag.)
  // Filter out any interaction the backend has already told us is terminal (accepted-elsewhere /
  // cancelled / expired / resolved) so a stale poll or a late duplicate can't re-ring it here.
  const incomingQueue = (workspace?.incoming_queue || []).filter(i => !(i && terminatedHandoffs[i.interaction_id]));
  const incomingInteraction = incomingQueue.find(i => i && i.accept_allowed !== false) || null;
  const lockedIncoming = incomingInteraction
    ? null
    : (incomingQueue.find(i => i && i.expired !== true) || incomingQueue[0] || null);
  const transferTargets = workspace?.available_staff_for_transfer || [];
  const callState = activeInteraction
    ? (activeInteraction.on_hold ? 'on_hold' : 'on_call')
    : (incomingInteraction ? 'ringing' : (lockedIncoming ? 'incoming_locked' : 'idle'));
  // Honest label for a non-actionable incoming item (why this operator can't answer it).
  const handoffLockText = (item) => {
    const r = item?.non_actionable_reason;
    if (item?.expired || r === 'expired') return t('od.handoffExpired');
    if (r === 'assigned_to_other_operator') return t('od.assignedOther');
    if (r === 'already_accepted') return t('od.alreadyAcceptedOther');
    if (r === 'no_longer_available') return t('od.noLongerAvailable');
    return t('od.notEligible');
  };
  // A media SESSION exists (gates mute/volume controls — enabled as soon as a call object/stream exists).
  const hasMedia = !!(webRTCCurrentCall || remoteStream);
  // MEDIA-TRUTH (P0-E): a real, live REMOTE audio track is actually being received. This — not merely
  // "a call object exists" — is what shows "Human Call Active" vs the honest "Connected — no live audio".
  const hasLiveAudio = !!hasRemoteAudio;

  // Audible + persistent incoming-handoff RING: loop a soft tone while an ACTIONABLE handoff is ringing
  // for THIS operator; stop immediately on accept / accepted-elsewhere / cancel / expiry / idle. WebAudio
  // oscillator (no asset). Honors browser autoplay policy — if audio is blocked the visual ringing card
  // (rendered on callState==='ringing') still stays prominent and actionable, so nothing is faked.
  // ONE AudioContext owned by THIS ringing period. Cleanup is IDEMPOTENT and state-guarded: close()
  // returns a Promise that REJECTS asynchronously when the context is already closed, so we (a) never
  // close twice (closed flag), (b) never close a non-open context (state check), and (c) always swallow
  // the async rejection (Promise.resolve(...).catch) — the exact defects behind the
  // "Cannot close a closed AudioContext" InvalidStateError. React StrictMode's double-invoke is safe
  // (each run makes its own ctx and its own cleanup); a later new handoff rings again with a fresh ctx.
  const ringAudioRef = useRef(null);
  const sharedRingCtxRef = useRef(null);                    // ONE gesture-unlocked AudioContext, reused across rings
  const [audioBlocked, setAudioBlocked] = useState(false);  // true when a handoff is ringing but autoplay muted the tone
  // Create/resume the shared ring AudioContext. Browser autoplay policy requires this to be reachable
  // from a real user gesture — it is called from the first desk interaction (effect below) and from the
  // explicit "Enable sound" button, so a desk that received a handoff before any click can still be unlocked.
  const unlockRingAudio = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!sharedRingCtxRef.current || sharedRingCtxRef.current.state === 'closed') sharedRingCtxRef.current = new AC();
      const ctx = sharedRingCtxRef.current;
      const settle = () => setAudioBlocked(ctx.state !== 'running');
      if (ctx.state === 'suspended') ctx.resume().then(settle).catch(() => setAudioBlocked(true));
      else settle();
    } catch (_) { /* audio unavailable — visual ring still shows, nothing faked */ }
  }, []);
  // Autoplay unlock: the FIRST pointer/key interaction with the desk pre-warms audio so the very NEXT
  // handoff rings audibly (the live failure: card shown but silent because the AudioContext was never
  // resumed by a gesture). Once-only; the explicit button covers the no-gesture-yet edge case.
  useEffect(() => {
    const h = () => unlockRingAudio();
    window.addEventListener('pointerdown', h, { once: true });
    window.addEventListener('keydown', h, { once: true });
    return () => { window.removeEventListener('pointerdown', h); window.removeEventListener('keydown', h); };
  }, [unlockRingAudio]);
  // Audible + persistent incoming-handoff RING while callState==='ringing'. Reuses the shared gesture-
  // unlocked context when present (so a prior interaction makes THIS ring audible); else a period-local
  // context is created and resume() attempted. If audio stays blocked, `audioBlocked` drives the visible
  // "Enable sound" affordance — the visual ring is never suppressed, nothing is faked. Cleanup is
  // idempotent + state-guarded (never double-close), and NEVER closes the shared unlocked context.
  useEffect(() => {
    if (callState !== 'ringing') { setAudioBlocked(false); return undefined; }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined;
    let ctx = sharedRingCtxRef.current;
    let ownCtx = false;
    if (!ctx || ctx.state === 'closed') { try { ctx = new AC(); ownCtx = true; } catch (_) { return undefined; } }
    let timer = null, closed = false;
    const closeCtx = () => {
      if (closed) return;
      closed = true;
      if (timer) { clearInterval(timer); timer = null; }
      if (ownCtx && ctx && ctx.state !== 'closed') { Promise.resolve(ctx.close()).catch(() => {}); }
    };
    ringAudioRef.current = { stop: closeCtx };
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    setAudioBlocked(ctx.state !== 'running');
    const beep = () => {
      if (closed || !ctx) return;
      if (ctx.state !== 'running') { setAudioBlocked(true); return; }
      setAudioBlocked(false);
      try {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
        o.connect(g); g.connect(ctx.destination);
        o.start(); o.stop(ctx.currentTime + 0.55);
      } catch (_) { /* transient node error — next tick retries */ }
    };
    beep();
    timer = setInterval(beep, 1800);
    return () => { closeCtx(); ringAudioRef.current = null; };
  }, [callState]);

  const cannedResponses = [
    { id: 1, text: "Thank you for contacting us. I'll be happy to help you with that.", category: 'greeting' },
    { id: 2, text: "I understand your frustration. Let me look into this immediately.", category: 'empathy' },
    { id: 3, text: "I've resolved your issue. Is there anything else I can help you with?", category: 'resolution' },
    { id: 4, text: "I need to escalate this to our technical team. You'll hear back within 24 hours.", category: 'escalation' }
  ];

  // Load real data on mount / when businessId changes
  useEffect(() => {
    if (!businessId) return;

    // Queue counts
    makeAuthenticatedRequest(`/api/v1/analytics/business/${businessId}/queues`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setQueues(data); })
      .catch(() => {});

    // Approved solutions for KB list
    makeAuthenticatedRequest(`/api/v1/solutions?business_id=${businessId}&status=approved&limit=20`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.solutions) setKnowledgeBase(data.solutions); })
      .catch(() => {});

    // Active escalations assigned to current staff
    makeAuthenticatedRequest(`/api/v1/escalations/business/${businessId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.escalations) setActiveTickets(data.escalations);
      })
      .catch(() => {});
  }, [businessId, makeAuthenticatedRequest]);

  // Slice 2: real operator-workspace — availability/break, live channel counts, transfer targets.
  const fetchWorkspace = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/operator-workspace/${businessId}`);
      if (!res.ok) return;
      const data = await res.json();
      setWorkspace(data);
      // Reflect server-side break state into the local status pill (display only).
      if (data?.break_status === 'on_break') setCallStatus('break');
    } catch (_) { /* keep last-known workspace on transient failure */ }
  }, [businessId, makeAuthenticatedRequest]);

  // Persist availability/break to the server; only reflect the UI after a 2xx (no local-only success).
  const updateOperatorStatus = useCallback(async (patch) => {
    if (!businessId) return;
    try {
      const res = await makeAuthenticatedRequest(`/api/v1/operator-workspace/${businessId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        toast({ title: t('od.statusUpdateFailed'), variant: 'destructive' });
        return;
      }
      if (patch.break_status === 'on_break') setCallStatus('break');
      else if (patch.break_status === 'off_break' || patch.availability_status === 'available') setCallStatus('available');
      fetchWorkspace();
    } catch (_) {
      toast({ title: t('od.statusUpdateFailed'), variant: 'destructive' });
    }
  }, [businessId, makeAuthenticatedRequest, fetchWorkspace, toast, t]);

  useEffect(() => {
    fetchWorkspace();
    const wsPoll = setInterval(fetchWorkspace, 8000);
    return () => clearInterval(wsPoll);
  }, [fetchWorkspace]);

  // P0-F: bind the ACCEPTED interaction's canonical ticket as the active ticket automatically, so
  // "Save & Resolve" always has a ticket to act on (the live failure: accepted interaction carried
  // ticket_id TKT-… yet Save & Resolve reported "No Active Ticket"). Keeps currentTicket in sync with
  // whichever interaction is currently accepted; never overrides a manual selection for the SAME id.
  const boundInteractionId = workspace?.active_interaction?.interaction_id;
  const boundTicketId = workspace?.active_interaction?.ticket_id;
  useEffect(() => {
    const ai = workspace?.active_interaction;
    if (!ai || !ai.ticket_id) return;
    setCurrentTicket((prev) => {
      if (prev && prev.interaction_id === ai.interaction_id && prev.ticket_id === ai.ticket_id) return prev;
      return {
        ...(prev && prev.interaction_id === ai.interaction_id ? prev : {}),
        ticket_id: ai.ticket_id,
        interaction_id: ai.interaction_id,
        conversation_id: ai.conversation_id || (prev && prev.conversation_id) || undefined,
        customer_id: ai.customer_id || (prev && prev.customer_id) || undefined,
        customer: ai.customer_display_name || ai.customer_name || (prev && prev.customer) || undefined,
        status: 'receiving_attention',
      };
    });
  }, [boundInteractionId, boundTicketId]);

  // Load staff identity and KPIs once staff record is resolved
  useEffect(() => {
    if (!businessId) return;
    makeAuthenticatedRequest(`/api/v1/staff/business/${businessId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.staff?.length) return;
        const uid = currentUser?.uid;
        // Guard each clause with the source value being truthy. Previously `s.uid === uid` with an
        // undefined uid matched the FIRST staff record (records have firebase_uid, not uid), which
        // selected another staff member (a manager) — the cause of the KPI 403. Display only.
        const matched = data.staff.find(s =>
          (uid && (s.firebase_uid === uid || s.uid === uid)) ||
          (currentUser?.email && s.email?.toLowerCase() === currentUser.email.toLowerCase()) ||
          (currentUser?.phoneNumber && s.phone === currentUser.phoneNumber.replace(/[\s\-().]/g, ''))
        );
        const me = matched || (uid ? { id: uid, firebase_uid: uid, name: currentUser?.displayName || 'You', role: 'operator' } : null);
        if (me) setStaffRecord(me);
        {
          // KPIs via the self endpoint — the backend resolves the operator from the VERIFIED token,
          // so we never send (and can never mis-pick) another staff member's id. No client id at all.
          console.log('[UAD_KPI] using /operator/me/kpis');  // temporary live-bundle proof marker
          makeAuthenticatedRequest(
            `/api/v1/analytics/operator/me/kpis?business_id=${businessId}&period=today`
          )
            .then(r => r.ok ? r.json() : null)
            .then(kpi => {
              if (!kpi) return;
              const ahtMin = kpi.aht_minutes || 0;
              const mm = Math.floor(ahtMin);
              const ss = String(Math.round((ahtMin - mm) * 60)).padStart(2, '0');
              setPersonalKPIs({
                callsToday:      kpi.calls_today ?? 0,
                aht:             ahtMin > 0 ? `${mm}:${ss}` : '—',
                fcr:             kpi.fcr_percent ?? '—',
                csat:            kpi.csat ?? '—',
                ticketsResolved: kpi.tickets_resolved ?? 0,
                target:          kpi.target || { aht: '5:00', fcr: 85, csat: 4.5 },
              });
              if (kpi.active_tickets > 0) {
                const myEsc = activeTickets.find(e =>
                  me?.id && e.assigned_to === me.id && e.status === 'receiving_attention'
                );
                if (myEsc) setCurrentTicket(myEsc);
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [businessId, currentUser, makeAuthenticatedRequest, activeTickets]);

  // Persist an interaction control transition to the backend. Returns the parsed 2xx body or throws
  // (never a local-only success). Callers refresh the workspace after so UI reflects real state.
  const postInteractionControl = async (interactionId, action, body) => {
    const resp = await makeAuthenticatedRequest(
      `/api/v1/operator-workspace/${businessId}/interactions/${interactionId}/${action}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      const detail = err.detail;
      const reason = (detail && typeof detail === 'object') ? detail.reason : detail;
      const answeredBy = (detail && typeof detail === 'object') ? detail.answered_by : undefined;
      const e = new Error(reason || `HTTP ${resp.status}`);
      e.status = resp.status;
      e.reason = reason;
      e.answeredBy = answeredBy;
      throw e;
    }
    return resp.json().catch(() => ({}));
  };

  // ------------------------------------------------------------------
  // ADDITIVE: in-app operator voice call — ring / answer / decline / end.
  // Reuses the shared WebRTC signaling socket (operator-call-invite / operator-call-cancel) and the
  // new backend /operator-call/* endpoints. Does NOT replace the Twilio phone bridge (that remains
  // the fallback when no operator is reachable in-app). No fake media: on Answer we bind the operator
  // to the WebRTC session; live audio flows when the caller's media offer arrives (media_capability
  // is reported honestly and never claimed as connected here).
  // ------------------------------------------------------------------
  const [opCallInvite, setOpCallInvite] = useState(null);   // incoming operator-call-invite (ringing)
  const [opCallActive, setOpCallActive] = useState(null);   // accepted session (this device won)
  const [opCallBusy, setOpCallBusy] = useState(false);

  // An invite that rang on another route was held app-wide; take it ONCE on arrival and seed the normal
  // card, so Answer runs the same canonical accept. No auto-answer.
  useEffect(() => {
    if (typeof claimOperatorCallRing !== 'function' || !operatorCallRing) return;
    const pending = claimOperatorCallRing();
    if (!pending) return;
    setOpCallInvite((prev) => (prev?.call_session_id === pending.call_session_id ? prev : pending));
  }, [operatorCallRing, claimOperatorCallRing]);

  useEffect(() => {
    if (typeof setExternalOperatorCallHandler !== 'function') return;
    setExternalOperatorCallHandler((msg) => {
      if (!msg || typeof msg.type !== 'string') return;
      if (msg.type === 'operator-call-invite') {
        setOpCallInvite((prev) => (prev?.call_session_id === msg.call_session_id ? prev : msg));
      } else if (msg.type === 'operator-call-cancel') {
        // Another of my devices (or another operator) took it, or it ended/timed out.
        setOpCallInvite((prev) => (prev && prev.call_session_id === msg.call_session_id ? null : prev));
        setOpCallActive((prev) => (prev && prev.call_session_id === msg.call_session_id ? null : prev));
        if (msg.reason === 'answered_elsewhere') {
          toast({ title: t('od.opCallAnsweredElsewhere'), description: t('od.opCallAnsweredElsewhereDesc') });
        }
      }
    });
    return () => { if (typeof setExternalOperatorCallHandler === 'function') setExternalOperatorCallHandler(null); };
  }, [setExternalOperatorCallHandler, toast, t]);

  // ADDITIVE: real-time AI human-handoff alert. When the backend routes a customer AI escalation to
  // this tenant's operators it pushes `operator-handoff-incoming` over the shared signaling socket;
  // we refresh the workspace immediately (so the queued card + Answer appear at once instead of on the
  // 20s poll) and surface a toast. Purely a refresh trigger — the authoritative queue is the fetched
  // /operator-workspace state, so this never fabricates an interaction.
  useEffect(() => {
    if (typeof setExternalHandoffHandler !== 'function') return;
    setExternalHandoffHandler((msg) => {
      if (!msg || typeof msg.type !== 'string') return;
      if (msg.type === 'operator-handoff-incoming') {
        try {
          toast({
            title: (msg.priority === 'urgent') ? t('od.handoffToastUrgent') : t('od.handoffToast'),
            description: msg.customer_name || t('od.opCallCustomer'),
          });
        } catch (_) { /* toast best-effort */ }
        fetchWorkspace();
      } else if (msg.type === 'operator-handoff-cancel') {
        // Another operator won this broadcast handoff — refresh so it drops out of our incoming queue.
        fetchWorkspace();
      } else if (TERMINAL_HANDOFF_STATUS[msg.type]) {
        // Backend terminal contract: operator-handoff-accepted|cancelled|expired|resolved carrying
        // { interaction_id, status, accepted_operator_id, ... }. On a terminal event for a ringing
        // interaction: stop the ring + clear the card + mark non-actionable (achieved by removing it
        // from our incoming queue), de-duped by interaction_id+status so a late copy can't re-ring.
        const iid = msg.interaction_id;
        if (!iid) return;
        const status = msg.status || TERMINAL_HANDOFF_STATUS[msg.type];
        setTerminatedHandoffs(prev => (prev[iid] === status ? prev : { ...prev, [iid]: status }));
        fetchWorkspace();
      }
    });
    return () => { if (typeof setExternalHandoffHandler === 'function') setExternalHandoffHandler(null); };
  }, [setExternalHandoffHandler, fetchWorkspace, toast, t]);

  // Prune the terminal ledger once the server no longer lists that interaction as incoming, so it can
  // never grow unbounded and a genuinely new interaction (server-re-listed) is never wrongly suppressed.
  useEffect(() => {
    const live = new Set((workspace?.incoming_queue || []).map(i => i && i.interaction_id).filter(Boolean));
    setTerminatedHandoffs(prev => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      let changed = false;
      const next = {};
      for (const k of keys) { if (live.has(k)) next[k] = prev[k]; else changed = true; }
      return changed ? next : prev;
    });
  }, [workspace]);

  const postOperatorCall = async (sessionId, action, body) => {
    const resp = await makeAuthenticatedRequest(
      `/api/v1/operator-call/${businessId}/sessions/${sessionId}/${action}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, status: resp.status, data };
  };

  const answerOperatorCall = async () => {
    if (opCallBusy || !opCallInvite?.call_session_id) return;
    setOpCallBusy(true);
    try {
      // NAME THE SOCKET THAT WILL CARRY THE MEDIA.
      //
      // Without it the server falls back to the operator ref and cancels every connection for this
      // operator - including the winning one - so the accept tears down its own call. Refusing here is
      // deliberate: a bodyless accept is not a safe degraded mode, it is the defect. No socket means no
      // media could flow anyway, so there is nothing to lose by declining to ring falsely.
      const connId = typeof getLiveConnectionId === 'function' ? getLiveConnectionId() : null;
      // ONE canonical accept, shared with the app-wide alert (lib/operatorCallAccept).
      const { outcome, status, data } = await acceptOperatorCallSession({
        request: makeAuthenticatedRequest,
        businessId,
        sessionId: opCallInvite.call_session_id,
        connectionId: connId,
      });
      if (outcome === 'no_connection') {
        setOpCallBusy(false);
        toast({ title: t('od.opCallFailed'), description: t('od.opCallNoConnection'), variant: 'destructive' });
        return;
      }
      if (outcome === 'won') {
        // This device won the race. Bind the session, then ARM THE MEDIA ROOM.
        //
        // The comment this replaces said media "joins when the caller's WebRTC offer arrives". It
        // does not, and never did: `shouldAnswerAsMediaOffer` answers an inbound offer in place ONLY
        // when `answererArmedRef`/`mediaRoomRef` are set, and nothing on this path set them. The
        // offer therefore fell through to `handleIncomingOffer`, which rang the GLOBAL
        // IncomingCallModal — so one encounter cost the provider TWO separate accepts, and the
        // operator-call card was not the surface that carried the media. Runtime-traced 2026-09-05.
        //
        // `joinMediaRoom` is the SAME transition the AI-Voice handoff accept already uses
        // (handleCallAction 'answer' -> res.media_join -> joinMediaRoom). No new stack, no new room
        // engine, no new accept authority — only the missing arm step, placed AFTER the win so a
        // loser can never arm media.
        setOpCallActive({ ...opCallInvite });
        setOpCallInvite(null);
        joinMediaRoom(
          {
            // Room identity is the SERVER's, from the accept response; the invite is only a fallback
            // for an older backend that omits it. Never a client-chosen room.
            call_id: data?.call_id || data?.call_session_id || opCallInvite.call_session_id,
            // What KIND of call this is, is likewise the server's statement (session.call_type).
            // joinMediaRoom opens a camera only for 'video', so an audio call stays mic-only.
            call_type: data?.session?.call_type || opCallInvite.call_type || 'voice',
            conversation_id: data?.session?.conversation_id || opCallInvite.conversation_id,
          },
          {
            businessId: currentBusiness?.brandId || businessId,
            customerName: opCallInvite.customer_name || t('od.opCallCustomer'),
          });
        toast({ title: t('od.opCallAnswered'), description: t('od.opCallConnecting') });
      } else if (status === 409) {
        // Lost the race — first-accept-wins. Honest, no fake success.
        setOpCallInvite(null);
        toast({ title: t('od.opCallAnsweredElsewhere'), description: t('od.opCallAnsweredElsewhereDesc') });
      } else {
        toast({ title: t('od.actionFailed'), description: (data && data.detail) || `HTTP ${status}`, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: t('od.actionFailed'), description: e?.message || t('od.networkError'), variant: 'destructive' });
    } finally {
      setOpCallBusy(false);
    }
  };

  const declineOperatorCall = async () => {
    try { if (typeof clearOperatorCallRinging === 'function') clearOperatorCallRinging(); } catch (e) { /* ringtone is best-effort */ }
    if (opCallBusy || !opCallInvite?.call_session_id) return;
    setOpCallBusy(true);
    const sid = opCallInvite.call_session_id;
    setOpCallInvite(null);
    try { await postOperatorCall(sid, 'decline'); } catch (e) { /* honest best-effort */ }
    finally { setOpCallBusy(false); }
  };

  const endOperatorCall = async () => {
    if (!opCallActive?.call_session_id) return;
    const sid = opCallActive.call_session_id;
    setOpCallActive(null);
    if (webRTCCurrentCall) webRTCEndCall();
    try { await postOperatorCall(sid, 'end'); } catch (e) { /* honest best-effort */ }
  };

  const handleCallAction = async (action) => {
    if (controlBusy) return;
    if (action === 'transfer') { setTransferDialogOpen(true); return; }
    setControlBusy(true);
    try {
      switch (action) {
        case 'answer': {
          const target = incomingInteraction || activeInteraction;
          if (!target?.interaction_id) {
            toast({ title: t('od.noIncomingCall'), description: t('od.noIncomingCallDesc'), variant: "destructive" });
            break;
          }
          const res = await postInteractionControl(target.interaction_id, 'accept');
          // ADDITIVE (Phase C): AI-Voice handoff carries a media_join contract → join the shared room as
          // ANSWERER and wait for the customer's offer (reuses the singleton signaling socket). This is
          // the ONLY media path for a voice handoff; do not also run the browser-call acceptCall().
          if (res?.media_join) {
            joinMediaRoom(res.media_join, {
              interactionId: target.interaction_id,
              businessId: currentBusiness?.brandId || businessId,
              // Display identity for the connected active-call UI comes from the ACCEPTED interaction
              // (customer_display_name), never the raw media_join model — prevents "User null".
              customerName: target.customer_display_name || target.customer_name || t('od.opCallCustomer'),
            });
            toast({ title: t('od.answered'), description: t('od.mediaJoining') });
          } else {
            // Join the browser media leg only if a real WebRTC session is present.
            if (webRTCCurrentCall) webRTCAcceptCall();
            const cap = res?.media_capability;
            if (cap && cap !== 'requires_webrtc_session' && cap !== 'browser_local_only') {
              toast({ title: t('od.answered'), description: t('od.answeredNoMedia') });
            } else {
              toast({ title: t('od.answered'), description: t('od.answeredDesc') });
            }
          }
          await fetchWorkspace();
          break;
        }
        case 'hangup': {
          if (!activeInteraction?.interaction_id) {
            toast({ title: t('od.noActiveCall'), description: t('od.noActiveCallDesc'), variant: "destructive" });
            break;
          }
          await postInteractionControl(activeInteraction.interaction_id, 'end');
          if (webRTCCurrentCall) webRTCEndCall();
          toast({ title: t('od.callEnded'), description: t('od.callDisconnected') });
          await fetchWorkspace();
          break;
        }
        case 'hold': {
          if (!activeInteraction?.interaction_id) break;
          const onHold = !!activeInteraction.on_hold;
          await postInteractionControl(activeInteraction.interaction_id, onHold ? 'resume' : 'hold');
          // Browser-local media hold: mute the local audio track while held (honest, best-effort).
          if (hasMedia) webRTCToggleMute();
          toast(onHold
            ? { title: t('od.callResumed'), description: t('od.callResumedDesc') }
            : { title: t('od.callOnHold'), description: t('od.callOnHoldDesc') });
          await fetchWorkspace();
          break;
        }
        default:
          break;
      }
    } catch (e) {
      // Honest, self-clearing outcomes for a lost/stale accept race: another operator won (409) or the
      // ring expired (410). Refresh so the card drops instead of leaving a dead incoming call.
      if (e?.status === 409) {
        toast({ title: t('od.alreadyAcceptedOther'),
                description: e?.answeredBy || t('od.alreadyAcceptedOther'), variant: "destructive" });
        await fetchWorkspace();
      } else if (e?.status === 410) {
        toast({ title: t('od.handoffExpired'), variant: "destructive" });
        await fetchWorkspace();
      } else {
        toast({ title: t('od.actionFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
      }
    } finally {
      setControlBusy(false);
    }
  };

  const handleTransferCall = async () => {
    if (!transferTarget) {
      toast({ title: t('od.transferTargetRequired'), description: t('od.transferTargetRequiredDesc'), variant: "destructive" });
      return;
    }
    if (!activeInteraction?.interaction_id) {
      toast({ title: t('od.noActiveCall'), description: t('od.noActiveCallTransfer'), variant: "destructive" });
      setTransferDialogOpen(false);
      return;
    }
    setControlBusy(true);
    try {
      // Real reassignment through the operator-workspace contract (backend enforces same-tenant +
      // target availability + source ownership). Success only after 2xx.
      const res = await postInteractionControl(activeInteraction.interaction_id, 'transfer', {
        target_operator_id: transferTarget,
        notes: transferNotes,
      });
      if (webRTCCurrentCall) webRTCEndCall();   // drop the local media leg; the target picks up their own
      const toName = res?.interaction?.transferred_to_display_name
        || (transferTargets.find(s => s.operator_id === transferTarget)?.display_name)
        || t('od.staffMemberFallback');
      toast({ title: t('od.callTransferred'), description: t('od.callTransferredDesc', { target: toName }) });
      setTransferDialogOpen(false);
      setTransferTarget('');
      setTransferNotes('');
      await fetchWorkspace();
    } catch (e) {
      toast({ title: t('od.transferFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setControlBusy(false);
    }
  };

  // Attach the live remote stream to the audio element so call audio actually plays.
  useEffect(() => {
    if (remoteAudioRef.current && remoteStream) {
      remoteAudioRef.current.srcObject = remoteStream;
      remoteAudioRef.current.volume = volumeLevel / 100;
    }
  }, [remoteStream, volumeLevel]);

  const handleVolumeChange = (newVolume) => {
    setVolumeLevel(newVolume);
    if (remoteAudioRef.current) {
      remoteAudioRef.current.volume = newVolume / 100;
    }
  };

  // Mute is browser-local: it toggles the real WebRTC audio track. We never fake a backend mute.
  // When an active interaction exists we best-effort persist the intent (no success toast — the
  // audible action is the local track); with no media session we say so honestly.
  const toggleMute = () => {
    if (!hasMedia) {
      toast({ title: t('od.noActiveAudio'), description: t('od.noActiveAudioDesc'), variant: "destructive" });
      return;
    }
    const muted = webRTCToggleMute();
    setIsMuted(!!muted);
    if (activeInteraction?.interaction_id) {
      // browser_local_only on the backend — persist state, but the mute already happened locally.
      postInteractionControl(activeInteraction.interaction_id, muted ? 'mute' : 'unmute').catch(() => {});
    }
  };

  const insertCannedResponse = (response) => {
    setQuickResponse(response.text);
  };

  // KB: use a real solution — insert its resolution text into the reply box (real action, no fake).
  const handleUseSolution = (item) => {
    const text = item.resolution || item.solution || item.content || item.description || item.answer || item.title || '';
    if (!text) {
      toast({ title: t('od.noSolutionText'), description: t('od.noSolutionTextDesc'), variant: 'destructive' });
      return;
    }
    setQuickResponse(text);
    toast({ title: t('od.solutionInserted'), description: t('od.solutionInsertedDesc', { title: item.title || t('od.solution') }) });
  };

  // KB search filter (title/category/text) — real client-side filter over fetched solutions.
  const filteredKnowledge = (knowledgeBase || []).filter((item) => {
    const q = kbSearch.trim().toLowerCase();
    if (!q) return true;
    return [item.title, item.category, item.resolution, item.description, item.content]
      .some((f) => (f || '').toLowerCase().includes(q));
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'voice': return <Phone className="h-4 w-4" />;
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'social': return <Users className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getCallStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'on_call': return 'bg-red-100 text-red-800';
      case 'on_hold': return 'bg-yellow-100 text-yellow-800';
      case 'break': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Productivity Tools Handlers
  const handleCreateTicket = async () => {
    if (!newTicket.title || !newTicket.description) {
      toast({ title: t('od.missingInformation'), description: t('od.fillTitleDescription'), variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: newTicket.customer || 'unknown',
          business_id: businessId,
          business_name: currentBusiness?.name || '',
          category: newTicket.category,
          initial_message: `${newTicket.title}: ${newTicket.description}`,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      toast({ title: t('od.ticketCreated'), description: t('od.ticketCreatedDesc', { id: data.ticket_id || (data.conversation_id ? String(data.conversation_id).slice(-6).toUpperCase() : '') }) });
      setNewTicket({ title: '', description: '', priority: 'medium', category: 'technical', customer: '' });
      setCreateTicketOpen(false);
    } catch (e) {
      toast({ title: t('od.createFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    }
  };

  const handleScheduleCallback = async () => {
    if (!callbackData.customer || !callbackData.date || !callbackData.time) {
      toast({ title: t('od.missingInformation'), description: t('od.fillCustomerDateTime'), variant: "destructive" });
      return;
    }
    // Attach real linkage from the active interaction/context when present. Only include ids that
    // actually exist — backend resolves display names, enforces tenant scope, defaults the assignee,
    // and is the sole writer to db.callbacks. We never send display-name authority from the client.
    const linkage = {};
    const ticketId = currentTicket?.ticket_id;
    const escalationId = currentTicket?.escalation_id || currentTicket?.id;
    const conversationId = currentTicket?.conversation_id || currentTicket?.id;
    const interactionId = currentTicket?.interaction_id;
    const customerId = currentTicket?.customer_id;
    const operatorId = staffRecord?.id || staffRecord?.firebase_uid || currentUser?.uid;
    if (ticketId) linkage.ticket_id = ticketId;
    if (escalationId) linkage.escalation_id = escalationId;
    if (conversationId) linkage.conversation_id = conversationId;
    if (interactionId) linkage.interaction_id = interactionId;
    if (customerId) linkage.customer_id = customerId;
    if (operatorId) linkage.assigned_operator_id = operatorId;
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/callbacks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: businessId,
          customer_name: callbackData.customer,
          customer_phone: callbackData.phone,
          scheduled_date: callbackData.date,
          scheduled_time: callbackData.time,
          notes: callbackData.notes,
          ...linkage,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast({ title: t('od.callbackScheduled'), description: t('od.callbackScheduledDesc', { customer: callbackData.customer }) });
      setCallbackData({ customer: '', phone: '', date: '', time: '', notes: '' });
      setScheduleCallbackOpen(false);
    } catch (e) {
      toast({ title: t('od.callbackFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    }
  };

  // Generate Report — the backend builds + PERSISTS a report record from real interaction context and
  // returns inline content. Success only after 2xx; download exports the backend content, never a
  // locally fabricated one.
  const handleGenerateReport = async () => {
    if (!activeInteraction?.interaction_id) {
      toast({ title: t('od.noReportContext'), description: t('od.noReportContextDesc'), variant: "destructive" });
      return;
    }
    setReportBusy(true);
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/operator-workspace/${businessId}/interactions/${activeInteraction.interaction_id}/report`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            report_type: reportConfig.type,
            include_transcript: reportIncludes.transcript,
            include_ticket: reportIncludes.ticket,
            include_callback: reportIncludes.callback,
            include_resolution: reportIncludes.resolution,
            notes: reportNotes,
          }) });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setLastReport(data);
      toast({ title: t('od.reportGenerated'), description: data.report_title || t('od.reportGeneratedDesc') });
    } catch (e) {
      toast({ title: t('od.reportFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setReportBusy(false);
    }
  };

  const downloadReport = () => {
    if (!lastReport?.report_url_or_content) return;
    const blob = new Blob([lastReport.report_url_or_content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${lastReport.report_id || 'operator_report'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyReport = async () => {
    if (!lastReport?.report_url_or_content) return;
    try { await navigator.clipboard.writeText(lastReport.report_url_or_content);
      toast({ title: t('od.reportCopied') }); } catch (_) { /* clipboard unavailable */ }
  };

  // Screen Share — creates a REAL backend session request. It stays honestly
  // "pending_customer_acceptance"; we never claim "active" without the backend saying so.
  const handleScreenShare = async () => {
    if (!activeInteraction?.interaction_id) {
      toast({ title: t('od.screenShareRequiresInteraction'), description: t('od.screenShareRequiresInteractionDesc'), variant: "destructive" });
      return;
    }
    setScreenShareBusy(true);
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/operator-workspace/${businessId}/interactions/${activeInteraction.interaction_id}/screen-share/request`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: screenShareReason, mode: 'view' }) });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setScreenShareSession(data);
      toast({ title: t('od.screenShareRequested'), description: data.request_message || t('od.screenShareWaiting') });
    } catch (e) {
      toast({ title: t('od.screenShareFailed'), description: e?.message || t('od.screenShareFailedFallback'), variant: "destructive" });
    } finally {
      setScreenShareBusy(false);
    }
  };

  const cancelScreenShare = async () => {
    if (!screenShareSession?.session_id) return;
    setScreenShareBusy(true);
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/operator-workspace/${businessId}/screen-share/${screenShareSession.session_id}/cancel`,
        { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setScreenShareSession(data);
      toast({ title: t('od.screenShareCancelled') });
    } catch (e) {
      toast({ title: t('od.screenShareFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setScreenShareBusy(false);
    }
  };

  // ── Customer communication quick-tools ────────────────────────────────────────────────────────
  // Real, backend-gated actions on the ACTIVE customer. Context prefers the server-authoritative
  // active interaction, falling back to the selected escalation/ticket. The interaction record does
  // not carry phone/email, so those are resolved on demand from the customer's conversation. No raw
  // id is ever used as a display label; success is asserted only after a backend 2xx.
  const _activeCustomer = () => {
    const ai = activeInteraction;
    const ct = currentTicket;
    return {
      customerId: ai?.customer_id || ct?.customer_id || '',
      conversationId: ai?.conversation_id || ct?.conversation_id || ct?.id || '',
      ticketId: ai?.ticket_id || ct?.ticket_id || null,
      name: ai?.customer_display_name || ct?.customer || t('od.customer'),
      phone: ct?.customer_phone || '',
      email: ct?.customer_email || '',
      hasContext: !!(ai || ct),
    };
  };

  // Best-effort lookup of the active customer's phone/email from their conversation record.
  const _resolveActiveCustomerContact = async () => {
    const custId = _activeCustomer().customerId;
    if (!custId || !businessId) return null;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/conversations/business/${businessId}`);
      if (resp.ok) {
        const d = await resp.json();
        const conv = (d.conversations || []).find(c => c.customer_id === custId);
        if (conv) return { phone: conv.customer_phone || '', email: conv.customer_email || '' };
      }
    } catch (_) { /* best-effort; caller handles the empty (honest "unavailable") case */ }
    return null;
  };

  // "Call Back Customer" — schedules a REAL Servisfy callback (canonical db.callbacks). We never open a
  // browser/VoIP window or fake a live call. If no phone can be resolved, we stop with an honest reason.
  const handleCallActiveCustomer = async () => {
    const ctx = _activeCustomer();
    if (!ctx.hasContext) {
      toast({ title: t('od.noActiveInteraction'), description: t('od.acceptInteractionFirst'), variant: "destructive" });
      return;
    }
    let phone = ctx.phone;
    if (!phone) {
      const resolved = await _resolveActiveCustomerContact();
      phone = resolved?.phone || '';
      if (phone) setCurrentTicket(prev => prev ? { ...prev, customer_phone: phone } : prev);
    }
    if (!phone) {
      toast({ title: t('od.customerPhoneUnavailable'), description: t('od.customerPhoneUnavailableDesc'), variant: "destructive" });
      return;
    }
    setCommBusy(true);
    try {
      const linkage = {};
      if (ctx.ticketId) linkage.ticket_id = ctx.ticketId;
      if (ctx.conversationId) linkage.conversation_id = ctx.conversationId;
      if (ctx.customerId) linkage.customer_id = ctx.customerId;
      const operatorId = staffRecord?.id || staffRecord?.firebase_uid || currentUser?.uid;
      if (operatorId) linkage.assigned_operator_id = operatorId;
      const resp = await makeAuthenticatedRequest('/api/v1/callbacks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: businessId,
          customer_name: ctx.name,
          customer_phone: phone,
          scheduled_date: new Date().toISOString().split('T')[0],
          scheduled_time: new Date().toTimeString().slice(0, 5),
          notes: 'Callback from Operator Desk',
          ...linkage,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast({ title: t('od.callbackScheduled'), description: t('od.callbackScheduledDesc', { customer: ctx.name }) });
    } catch (e) {
      toast({ title: t('od.callFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setCommBusy(false);
    }
  };

  // "Message Customer" — opens the quick-message dialog for the active customer's conversation thread.
  const handleMessageActiveCustomer = () => {
    const ctx = _activeCustomer();
    if (!ctx.hasContext) {
      toast({ title: t('od.noActiveInteraction'), description: t('od.acceptInteractionFirst'), variant: "destructive" });
      return;
    }
    if (!ctx.conversationId) {
      toast({ title: t('od.noConversationLinked'), description: t('od.noConversationLinkedDesc'), variant: "destructive" });
      return;
    }
    setQuickMessageText('');
    setQuickMessageOpen(true);
  };

  // Sends into the ONE customer conversation thread via the existing operator message contract
  // (sender_type/message_type 'operator') — the same payload Web/Android/iOS already render. NO new
  // message_type is introduced. Success only after a backend 2xx.
  const handleSendQuickMessage = async () => {
    if (!quickMessageText.trim()) {
      toast({ title: t('od.emptyMessage'), description: t('od.emptyMessageDesc'), variant: "destructive" });
      return;
    }
    const convId = _activeCustomer().conversationId;
    if (!convId) {
      toast({ title: t('od.noConversationLinked'), description: t('od.noConversationLinkedDesc'), variant: "destructive" });
      return;
    }
    setCommBusy(true);
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/conversations/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: convId,
          sender_id: staffRecord?.id || currentUser?.uid || 'staff',
          sender_type: 'operator',
          message: quickMessageText.trim(),
          message_type: 'operator',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('od.messageSent'), description: t('od.messageSentDesc') });
      setQuickMessageText('');
      setQuickMessageOpen(false);
    } catch (e) {
      toast({ title: t('od.sendFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setCommBusy(false);
    }
  };

  // "Email Customer" — resolves the customer email, then opens a real compose dialog. Kept honest:
  // if no email is on file we stop with a clear reason (the button stays visible/enabled to explain).
  const handleEmailActiveCustomer = async () => {
    const ctx = _activeCustomer();
    if (!ctx.hasContext) {
      toast({ title: t('od.noActiveInteraction'), description: t('od.acceptInteractionFirst'), variant: "destructive" });
      return;
    }
    let email = ctx.email;
    if (!email) {
      const resolved = await _resolveActiveCustomerContact();
      email = resolved?.email || '';
      if (email) setCurrentTicket(prev => prev ? { ...prev, customer_email: email } : prev);
    }
    if (!email) {
      toast({ title: t('od.customerEmailUnavailable'), description: t('od.customerEmailUnavailableDesc'), variant: "destructive" });
      return;
    }
    setUadEmailTo(email);
    setUadEmailData({ subject: '', body: '' });
    setUadEmailOpen(true);
  };

  // Sends the email via the existing POST /api/v1/emails/send contract. Success only after a 2xx.
  const handleSendUadEmail = async () => {
    const email = uadEmailTo;
    if (!email) { toast({ title: t('od.customerEmailUnavailable'), variant: "destructive" }); return; }
    if (!uadEmailData.subject || !uadEmailData.body) {
      toast({ title: t('od.missingFields'), description: t('od.emailFillSubjectBody'), variant: "destructive" });
      return;
    }
    setCommBusy(true);
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/emails/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: [email],
          subject: uadEmailData.subject,
          message: uadEmailData.body,
          business_id: businessId,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      toast({ title: t('od.emailSent'), description: t('od.emailSentDesc', { email }) });
      setUadEmailOpen(false);
      setUadEmailData({ subject: '', body: '' });
    } catch (e) {
      toast({ title: t('od.emailSendFailed'), description: e?.message || t('od.networkError'), variant: "destructive" });
    } finally {
      setCommBusy(false);
    }
  };

  const handleSaveResolve = async () => {
    console.log('UnifiedAgentDesktop: Save & Resolve clicked', {
      resolutionNotes: resolutionNotes.substring(0, 50),
      currentTicket,
      currentUser
    });

    if (!resolutionNotes.trim()) {
      toast({
        title: t('od.resolutionNotesRequired'),
        description: t('od.resolutionNotesRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    // Operator Solution Bank submission is OPT-IN and REQUIRES a meaningful reusable resolution. Validate
    // BEFORE resolving so a bad/low-quality candidate is caught up front (the backend re-validates via the
    // canonical resolution-quality gate and remains authoritative). No hidden content is auto-submitted.
    if (submitToSolutionBank && !isMeaningfulResolution(solutionResolutionSteps)) {
      toast({
        title: t('od.resolutionNotesRequired'),
        description: 'Enter meaningful Resolution Steps (not empty or placeholder) to submit this to the Solution Bank for manager review.',
        variant: "destructive",
      });
      return;
    }

    // P0-F: resolve against the ACCEPTED interaction's canonical ticket when no ticket was manually
    // selected. The accepted interaction carries ticket_id (e.g. TKT-…); use it (and its interaction_id)
    // so Save & Resolve no longer fails with "No Active Ticket".
    const interactionId = activeInteraction?.interaction_id || currentTicket?.interaction_id;
    const ticketId = currentTicket?.ticket_id || activeInteraction?.ticket_id || currentTicket?.id;
    if (!ticketId && !interactionId) {
      toast({ title: t('od.noActiveTicket'), description: t('od.selectTicketResolve'), variant: "destructive" });
      return;
    }

    try {
      if (ticketId) {
        // Ticket path — include interaction_id so the backend can reconcile the resolve to the handoff.
        const response = await makeAuthenticatedRequest(`/api/v1/tickets/${ticketId}/resolve`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolution: resolutionNotes, ...(interactionId ? { interaction_id: interactionId } : {}) }),
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      } else {
        // No ticket known client-side — resolve BY interaction; the backend resolves the canonical ticket.
        await postInteractionControl(interactionId, 'resolve', { resolution: resolutionNotes });
      }

      // Operator resolution -> Solution Bank candidate via the ONE canonical create endpoint. This is the
      // SAME funnel as manual "Add New Solution" and the escalation-resolve submit: create_solution enforces
      // the resolution-quality gate, forces status=pending_approval + submitted_by (from token) and stamps
      // source_channel. It is NEVER auto-approved/auto-published; a Manager/Admin must approve it. A submission
      // failure (e.g. 422 low-quality) is surfaced honestly and does NOT undo the ticket resolution.
      if (submitToSolutionBank) {
        try {
          const sbResp = await makeAuthenticatedRequest('/api/v1/solutions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              business_id: businessId,
              title: (solutionTitle.trim() || currentTicket?.subject || currentTicket?.intent || 'Resolved issue').slice(0, 80),
              description: resolutionNotes.trim().slice(0, 500),
              resolution: solutionResolutionSteps.trim(),
              category: 'General',
              source: 'operator_resolution',
              source_channel: activeInteraction ? 'voice_call' : 'human_chat',
            }),
          });
          if (!sbResp.ok) {
            const err = await sbResp.json().catch(() => ({}));
            const detail = err && err.detail;
            throw new Error(typeof detail === 'string' ? detail : (detail?.message || `HTTP ${sbResp.status}`));
          }
          toast({ title: t('od.ticketResolved'), description: 'Resolution submitted to the Solution Bank for manager review.' });
          setSubmitToSolutionBank(false);
          setSolutionTitle('');
          setSolutionResolutionSteps('');
        } catch (sbErr) {
          // The ticket IS resolved; only the reusable-knowledge submission failed. Tell the operator why.
          toast({ title: 'Solution not submitted', description: sbErr?.message || 'Could not submit to the Solution Bank.', variant: 'destructive' });
        }
      }

      toast({ title: t('od.ticketResolved'), description: t('od.ticketResolvedDesc', { id: ticketId || interactionId }) });
      setCurrentTicket(prev => prev ? { ...prev, status: 'resolved' } : null);
      // Drop the resolved item from the active voice queue + pending escalations (local), then refresh
      // server truth so it clears the accepted interaction / incoming queue too. Stay on the desk.
      setActiveTickets(prev => (prev || []).filter(e => !(
        (ticketId && (e.ticket_id === ticketId || e.id === ticketId)) ||
        (interactionId && e.interaction_id === interactionId)
      )));
      fetchWorkspace();
      // Reset form + status only on success (so a failed resolve keeps the notes for retry).
      setCallStatus('available');
      setResolutionNotes('');
      setSaveResolveDialogOpen(false);
    } catch (error) {
      toast({ title: t('od.resolutionFailed'), description: error?.message || t('od.networkError'), variant: "destructive" });
    }
  };

  const handleEscalate = async () => {
    console.log('UnifiedAgentDesktop: Escalate clicked', {
      escalationReason: escalationReason.substring(0, 50),
      currentTicket,
      currentUser
    });

    if (!escalationReason.trim()) {
      toast({
        title: t('od.escalationReasonRequired'),
        description: t('od.escalationReasonRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    const ticketId = currentTicket?.ticket_id || currentTicket?.id;
    if (!ticketId) {
      toast({ title: t('od.noActiveTicket'), description: t('od.selectTicketEscalate'), variant: "destructive" });
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/api/v1/tickets/${ticketId}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: escalationReason, priority: 'high', target_role: escalateTo }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({ title: t('od.escalated'), description: t('od.escalationCreatedDesc', { id: result.ticket_reference || (result.escalation_id ? 'ESC-' + String(result.escalation_id).slice(-6).toUpperCase() : '') }) });
        setCurrentTicket(prev => prev ? { ...prev, status: 'escalated' } : null);
      } else {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error) {
      toast({ title: t('od.escalationFailed'), description: error?.message, variant: "destructive" });
    }

    // Reset form and update status
    setCallStatus('escalated');
    setEscalationReason('');
    setEscalateDialogOpen(false);
  };

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Remote call audio sink — plays the caller's audio; Volume control adjusts this element. */}
      <audio ref={remoteAudioRef} autoPlay className="hidden" />

      {/* M4 — governed capture control for an ACTIVE encounter consultation. Renders ONLY when the
          active room id is `enc_<appointment_id>`; a staff-to-staff call never carries that
          identity, so normal Business calling is untouched. Audio and video consultations share
          this one control — the room id carries no mode. */}
      <EncounterCaptureBar
        activeCallId={activeCallId}
        localStream={localStream}
        connected={hasLiveAudio}
        businessId={businessId}
        fetchReadiness={captureApi.fetchReadiness}
        sendSegment={captureApi.sendSegment}
      />

      {/* ADDITIVE: in-app operator voice-call ring banner (first-accept-wins across the operator's
          devices; other devices clear on operator-call-cancel). Reuses the shared WebRTC socket. */}
      {opCallInvite && (
        <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-4 flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <PhoneCall className="h-5 w-5 text-green-600" />
            <div>
              {/* The invite has ALWAYS carried call_type; this surface simply never read it, so a
                  video consultation announced itself with the generic operator-call heading. The
                  server's value decides — an unknown/absent kind stays on the generic heading rather
                  than guessing "voice". */}
              <div className="font-semibold text-green-900">
                {opCallInvite.call_type === 'video'
                  ? t('od.opCallIncomingVideo')
                  : (opCallInvite.call_type === 'voice'
                      ? t('od.opCallIncomingVoice')
                      : t('od.opCallIncoming'))}
              </div>
              <div className="text-sm text-green-700">
                {opCallInvite.customer_name || t('od.opCallCustomer')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={opCallBusy} onClick={answerOperatorCall}>
              <Phone className="h-4 w-4 mr-1" />{opCallBusy ? t('od.opCallStarting') : t('od.answer')}
            </Button>
            <Button size="sm" variant="outline" disabled={opCallBusy} onClick={declineOperatorCall}>
              <PhoneOff className="h-4 w-4 mr-1" />{t('od.opCallDecline')}
            </Button>
          </div>
        </div>
      )}
      {opCallActive && !opCallInvite && (
        <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Headphones className="h-5 w-5 text-blue-600" />
            <div>
              <div className="font-semibold text-blue-900">
                {opCallActive.customer_name || t('od.opCallCustomer')}
              </div>
              {/* Was the hardcoded "Connecting — waiting for caller audio", which never changed even
                  once media was live. Now the REAL ladder, through the same pure resolver the
                  AI-Voice handoff banner uses (media-truth: 'human_call_active' still reads as
                  "Connected — no live audio" until a remote audio track actually arrives). Falls back
                  to the old string only before the ladder has reported anything. */}
              <div className="text-sm text-blue-700">
                {(resolveHandoffPhaseCfg(mediaPhase, hasLiveAudio) || {}).key
                  ? t(resolveHandoffPhaseCfg(mediaPhase, hasLiveAudio).key)
                  : t('od.opCallConnecting')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mute belongs to the surface that owns the call. The main control bar's Mute is gated on
                `activeInteraction` (the AI-Voice handoff plane), which an operator-call/encounter never
                populates — so it was unreachable here. Same canonical local-track toggle, no new state. */}
            <Button size="sm" variant="outline" disabled={!hasMedia} onClick={toggleMute}
                    title={!hasMedia ? t('od.noActiveAudio') : undefined}>
              {isMuted ? <MicOff className="h-4 w-4 mr-1" /> : <Mic className="h-4 w-4 mr-1" />}
              {isMuted ? t('od.unmute') : t('od.mute')}
            </Button>
            <Button size="sm" variant="destructive" onClick={endOperatorCall}>
              <PhoneOff className="h-4 w-4 mr-1" />{t('od.hangUp')}
            </Button>
          </div>
        </div>
      )}

      {/* Header with Status */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{t('nav.unifiedDesktop')}</h1>
            </div>
            <p className="text-gray-600">{t('od.subtitleUnifiedDesktop')}</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge className={getCallStatusColor(callStatus)}>
              {callStatus.replace('_', ' ').toUpperCase()}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateOperatorStatus(callStatus === 'break'
                ? { break_status: 'off_break', availability_status: 'available' }
                : { break_status: 'on_break', availability_status: 'busy' })}
            >
              {callStatus === 'break' ? t('od.endBreak') : t('od.takeBreak')}
            </Button>
          </div>
        </div>

        {/* Personal KPI Dashboard */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 mb-6">
          <CardContent className="p-4">
            <div className="grid md:grid-cols-6 gap-4">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-700">{personalKPIs.callsToday}</div>
                <div className="text-xs text-blue-600">{t('od.callsToday')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-700">{personalKPIs.aht}</div>
                <div className="text-xs text-green-600">{t('od.avgHandleTime')}</div>
                <div className="text-xs text-gray-500">{t('od.target', { value: kpiTarget(personalKPIs.target?.aht) })}</div>
              </div>
              <div className="text-center">
                {/* D16 — honest FCR: append '%' only to a real number; a missing metric stays '—' (no '—%'). */}
                <div className="text-lg font-bold text-purple-700">{typeof personalKPIs.fcr === 'number' ? `${personalKPIs.fcr}%` : personalKPIs.fcr}</div>
                <div className="text-xs text-purple-600">{t('od.firstCallResolution')}</div>
                <div className="text-xs text-gray-500">{t('od.target', { value: kpiTarget(personalKPIs.target?.fcr, '%') })}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-orange-700">{personalKPIs.csat}</div>
                <div className="text-xs text-orange-600">{t('od.customerSatisfaction')}</div>
                <div className="text-xs text-gray-500">{t('od.target', { value: kpiTarget(personalKPIs.target?.csat) })}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-teal-700">{personalKPIs.ticketsResolved}</div>
                <div className="text-xs text-teal-600">{t('od.ticketsResolved')}</div>
              </div>
              <div className="text-center">
                {/* D16 — the hardcoded "Excellent" is removed. No canonical backend performance score/band
                    exists, so this shows an honest unavailable state rather than a fabricated positive label. */}
                <Award className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                <div className="text-xs text-gray-500">{t('od.performanceUnavailable')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Channel Management */}
        <div className="lg:col-span-1">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg mb-6">
            <CardHeader>
              <CardTitle className="text-lg">{t('od.channelOverview')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(activeChannels).map(([channel, data]) => (
                <div 
                  key={channel}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    activeChannel === channel 
                      ? 'bg-gradient-to-r from-blue-100 to-purple-100 border border-purple-300'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveChannel(channel)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getChannelIcon(channel)}
                      <span className="font-medium capitalize">{t('od.channel' + channel.charAt(0).toUpperCase() + channel.slice(1))}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {data.active && !data.unavailable && <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>}
                      {data.unavailable
                        ? <Badge variant="outline" title={t('od.channelUnavailable')}>—</Badge>
                        : <Badge variant="outline">{data.queue}</Badge>}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Call Controls */}
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">{t('od.callControls')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleCallAction('answer')}
                  disabled={controlBusy || callState !== 'ringing'}
                  title={callState !== 'ringing' ? t('od.disabledNoIncoming') : undefined}
                >
                  <PhoneCall className="h-4 w-4 mr-2" />
                  {t('od.answer')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleCallAction('hangup')}
                  disabled={controlBusy || !activeInteraction}
                  title={!activeInteraction ? t('od.disabledNoActive') : undefined}
                >
                  <PhoneOff className="h-4 w-4 mr-2" />
                  {t('od.hangUp')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleCallAction('hold')}
                  disabled={controlBusy || !activeInteraction}
                  title={!activeInteraction ? t('od.disabledNoActive') : undefined}
                >
                  {callState === 'on_hold' ? (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      {t('od.resume')}
                    </>
                  ) : (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      {t('od.hold')}
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleCallAction('transfer')}
                  disabled={controlBusy || !activeInteraction || transferTargets.length === 0}
                  title={!activeInteraction ? t('od.disabledNoActive')
                        : transferTargets.length === 0 ? t('od.disabledNoTransferTargets') : undefined}
                >
                  <Users className="h-4 w-4 mr-2" />
                  {t('od.transfer')}
                </Button>
                <Button
                  variant={isMuted ? "default" : "outline"}
                  onClick={toggleMute}
                  disabled={!hasMedia}
                  title={!hasMedia ? t('od.disabledNoAudio') : undefined}
                  className="col-span-1"
                >
                  {isMuted ? <MicOff className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
                  {isMuted ? t('od.unmute') : t('od.mute')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setVolumeDialogOpen(true)}
                  disabled={!hasMedia}
                  title={!hasMedia ? t('od.disabledNoAudio') : undefined}
                  className="col-span-1"
                >
                  <Volume2 className="h-4 w-4 mr-2" />
                  {t('od.volume')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Interaction Area */}
        <div className="lg:col-span-2">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('od.customerInteraction', { channel: t('od.channel' + activeChannel.charAt(0).toUpperCase() + activeChannel.slice(1)) })}</span>
                {(callState === 'on_call' || callState === 'on_hold') && (
                  <Badge className={hasLiveAudio ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}>
                    <Clock className="h-3 w-3 mr-1" />
                    {callState === 'on_hold' ? t('od.onHold') : (hasLiveAudio ? t('od.live') : t('od.connectedNoAudio'))}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* ADDITIVE (Phase C): AI-Voice human-handoff honest media state + live transcript + grounded summary */}
              <HandoffMediaPanel
                interactionId={activeInteraction?.interaction_id || incomingInteraction?.interaction_id}
                businessId={currentBusiness?.brandId || businessId}
                mediaPhase={mediaPhase}
                hasLiveAudio={hasLiveAudio}
                makeAuthenticatedRequest={makeAuthenticatedRequest}
                t={t}
              />
              {activeChannel === 'voice' && (callState === 'on_call' || callState === 'on_hold') && (
                <div className="space-y-6">
                  {/* Current interaction info. HONEST live-state: a "Live Call" (pulsing red, live audio)
                      is shown ONLY when a real WebRTC media leg exists (hasMedia). An accepted voice
                      handoff with no customer media leg (Twilio real-voice disabled) is "Connected — no
                      live audio", NOT a fake live call. */}
                  <div className={`p-4 rounded-lg border ${hasLiveAudio
                    ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200'
                    : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${hasLiveAudio ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`}></div>
                        <span className={`font-semibold ${hasLiveAudio ? 'text-red-800' : 'text-amber-800'}`}>
                          {hasLiveAudio ? t('od.liveCall') : t('od.connectedNoAudio')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{activeChannels.voice.current?.duration}</div>
                    </div>
                    <div className="space-y-2">
                      <div className="font-medium text-gray-900">{activeChannels.voice.current?.customer}</div>
                      <div className="text-sm text-gray-600">{activeChannels.voice.current?.phone}</div>
                      <div className="text-sm"><strong>{t('od.issueLabel')}</strong> {activeChannels.voice.current?.issue}</div>
                      <Badge className="bg-yellow-100 text-yellow-800">{t('od.prioritySuffix', { priority: activeChannels.voice.current?.priority })}</Badge>
                    </div>
                  </div>

                  {/* Customer History */}
                  <div>
                    <h4 className="font-medium mb-3">{t('od.customerHistory')}</h4>
                    <div className="space-y-2">
                      {activeChannels.voice.current?.history?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <div className="text-sm font-medium">{item.issue}</div>
                            <div className="text-xs text-gray-600">{item.date}</div>
                          </div>
                          <Badge className={item.resolved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {item.resolved ? t('od.resolved') : t('od.pending')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Call Notes & Quick Response Area */}
                  <div>
                    <h4 className="font-medium mb-3">{t('od.callNotes')}</h4>
                    <Textarea
                      placeholder={t('od.addNotesPlaceholder')}
                      rows={4}
                      className="mb-3"
                    />
                    {/* Quick Response Input with Send & Paperclip */}
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="relative flex-1">
                        <Paperclip className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <Input
                          placeholder={t('od.quickResponsePlaceholder')}
                          value={quickResponse}
                          onChange={(e) => setQuickResponse(e.target.value)}
                          className="pl-10 pr-10"
                        />
                        <button
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-700"
                          onClick={async () => {
                            if (!quickResponse.trim()) return;
                            const convId = currentTicket?.conversation_id;
                            if (!convId) {
                              toast({ title: t('od.noConversationLinked'), description: t('od.noConversationLinkedDesc'), variant: "destructive" });
                              return;
                            }
                            try {
                              const r = await makeAuthenticatedRequest('/api/v1/conversations/message', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  conversation_id: convId,
                                  sender_id: staffRecord?.id || currentUser?.uid || 'staff',
                                  sender_type: 'operator',
                                  message: quickResponse,
                                  message_type: 'operator',
                                }),
                              });
                              if (!r.ok) throw new Error(`HTTP ${r.status}`);
                              toast({ title: t('od.messageSent'), description: t('od.messageSentDesc') });
                              setQuickResponse('');
                            } catch (e) {
                              toast({ title: t('od.sendFailed'), description: e?.message, variant: "destructive" });
                            }
                          }}
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {/* Agent Info with Avatar */}
                    <div className="flex items-center space-x-2 mb-3">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">{(staffRecord?.name || currentUser?.email || 'Me').split(' ').map(n => n[0]).join('').substring(0,2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-gray-500">{t('od.handlingAs', { name: staffRecord?.name || currentUser?.email || 'You' })}</span>
                    </div>
                    {/* Call Quality Progress Bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>{t('od.callQualityScore')}</span>
                        {/* D16 — honest Call Quality: derive from CSAT only when numeric; never render "NaN%". */}
                        <span>{typeof personalKPIs.csat === 'number' ? `${Math.round(personalKPIs.csat * 20)}%` : '—'}</span>
                      </div>
                      <Progress value={typeof personalKPIs.csat === 'number' ? personalKPIs.csat * 20 : 0} className="h-2" />
                    </div>
                    {/* Volume Muted Indicator */}
                    {isMuted && (
                      <div className="flex items-center space-x-1 text-xs text-red-500 mb-3">
                        <VolumeX className="h-3 w-3" />
                        <span>{t('od.micMuted')}</span>
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <Button 
                        className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                        onClick={() => setSaveResolveDialogOpen(true)}
                      >
                        {t('od.saveResolve')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEscalateDialogOpen(true)}
                      >
                        {t('od.escalate')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Incoming / queued human-handoff — actionable card. Renders whenever the backend
                  workspace has a pending interaction (incoming_queue[0]) even though no media call is
                  ringing yet: an AI handoff is a queued customer waiting for an operator, not a WebRTC
                  ring. Answer accepts it (first-accept-wins) via the operator-workspace accept endpoint. */}
              {callState === 'ringing' && incomingInteraction && (
                <div className="space-y-4 py-4">
                  <div className={`p-4 rounded-lg border ${((incomingInteraction.priority || 'normal') === 'urgent')
                    ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">{t('od.incomingHandoff')}</h3>
                      </div>
                      {((incomingInteraction.priority || 'normal') === 'urgent') && (
                        <Badge className="bg-red-100 text-red-800">{t('od.priorityUrgent')}</Badge>
                      )}
                    </div>
                    <div className="space-y-1 mb-4">
                      <div className="text-base font-medium text-gray-900">
                        {incomingInteraction.customer_display_name || incomingInteraction.customer_name || t('od.opCallCustomer')}
                      </div>
                      <div className="text-sm text-gray-600">
                        {t('od.handoffChannel', { channel: t('od.channel' + (incomingInteraction.channel || 'voice').charAt(0).toUpperCase() + (incomingInteraction.channel || 'voice').slice(1)) })}
                        {incomingInteraction.source ? ` · ${incomingInteraction.source === 'ai_voice' ? t('od.sourceAiVoice') : incomingInteraction.source === 'ai_chat' ? t('od.sourceAiChat') : incomingInteraction.source}` : ''}
                      </div>
                      {(incomingInteraction.transcript_summary || incomingInteraction.escalation_reason) && (
                        <div className="text-sm text-gray-700 pt-1">
                          <strong>{t('od.issueLabel')}</strong> {incomingInteraction.transcript_summary || incomingInteraction.escalation_reason}
                        </div>
                      )}
                      {(activeChannels[activeChannel]?.queue > 1) && (
                        <div className="text-xs text-gray-500 pt-1">
                          {t('od.queueWaiting', { count: activeChannels[activeChannel]?.queue })}
                        </div>
                      )}
                    </div>
                    {audioBlocked && (
                      <button
                        type="button"
                        onClick={unlockRingAudio}
                        className="w-full mb-2 text-sm px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                      >
                        🔔 {t('od.enableSound')}
                      </button>
                    )}
                    <div className="flex space-x-2">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={controlBusy}
                        onClick={() => handleCallAction('answer')}
                      >
                        <Phone className="h-4 w-4 mr-2" />{t('od.answer')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {callState === 'incoming_locked' && lockedIncoming && (
                <div className="space-y-4 py-4">
                  {/* Non-actionable incoming handoff — visible for awareness, but NOT this operator's to
                      answer (assigned to another operator / expired / ineligible). No enabled Answer. */}
                  <div className="p-4 rounded-lg border bg-gray-50 border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-gray-400"></span>
                        <h3 className="text-lg font-semibold text-gray-900">{t('od.incomingHandoff')}</h3>
                      </div>
                      <Badge className="bg-gray-200 text-gray-700">{handoffLockText(lockedIncoming)}</Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="text-base font-medium text-gray-900">
                        {lockedIncoming.customer_display_name || lockedIncoming.customer_name || t('od.opCallCustomer')}
                      </div>
                      <div className="text-sm text-gray-600">{handoffLockText(lockedIncoming)}</div>
                    </div>
                  </div>
                </div>
              )}

              {callState === 'idle' && (
                <div className="text-center py-12">
                  <Headphones className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('od.readyNext')}</h3>
                  <p className="text-gray-500 mb-4">{t('od.waitingIncoming', { channel: t('od.channel' + activeChannel.charAt(0).toUpperCase() + activeChannel.slice(1)) })}</p>
                  <div className="text-sm text-gray-600">
                    {t('od.queueWaiting', { count: activeChannels[activeChannel]?.queue })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tools & Knowledge Base */}
        <div className="lg:col-span-1">
          <Tabs defaultValue="knowledge" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="knowledge">{t('od.tabKB')}</TabsTrigger>
              <TabsTrigger value="responses">{t('od.tabQuick')}</TabsTrigger>
              <TabsTrigger value="tools">{t('od.tabTools')}</TabsTrigger>
            </TabsList>

            {/* Knowledge Base */}
            <TabsContent value="knowledge">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('od.knowledgeBase')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder={t('od.searchSolutions')}
                      className="pl-10"
                      value={kbSearch}
                      onChange={(e) => setKbSearch(e.target.value)}
                      data-testid="kb-search-input"
                    />
                  </div>
                  <div className="space-y-3">
                    {filteredKnowledge.length === 0 ? (
                      <div className="text-sm text-gray-500 text-center py-6">
                        {knowledgeBase.length === 0 ? t('od.noApprovedSolutions') : t('od.noSolutionsMatch')}
                      </div>
                    ) : filteredKnowledge.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                        onClick={() => handleUseSolution(item)}
                        title={t('od.clickToInsert')}
                        data-testid={`kb-solution-${item.id}`}
                      >
                        <div className="font-medium text-sm mb-1">{item.title}</div>
                        <div className="text-xs text-gray-600 mb-2">{item.category}</div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <Star className="h-3 w-3 text-yellow-500 fill-current" />
                            <span className="text-xs">{item.rating}</span>
                          </div>
                          {/* A usage count the Solution Bank did not send is omitted, never "undefined uses". */}
                          {typeof item.usage === 'number' && Number.isFinite(item.usage) ? (
                            <div className="text-xs text-gray-500">{t('od.usesSuffix', { count: item.usage })}</div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Canned Responses */}
            <TabsContent value="responses">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('od.quickResponses')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {cannedResponses.map((response) => (
                      <div 
                        key={response.id}
                        className="p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 text-xs"
                        onClick={() => insertCannedResponse(response)}
                      >
                        <div className="font-medium text-blue-600 mb-1">{response.category}</div>
                        <div className="text-gray-700">{response.text}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Productivity Tools */}
            <TabsContent value="tools">
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('od.productivityTools')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Dialog open={createTicketOpen} onOpenChange={setCreateTicketOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Zap className="h-4 w-4 mr-2" />
                        {t('od.createTicket')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('od.createNewTicket')}</DialogTitle>
                        <DialogDescription>
                          {t('od.createTicketDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.customer')}</label>
                          <Input
                            placeholder={t('od.customerNameEmailPlaceholder')}
                            value={newTicket.customer}
                            onChange={(e) => setNewTicket(prev => ({ ...prev, customer: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.titleRequired')}</label>
                          <Input
                            placeholder={t('od.briefIssuePlaceholder')}
                            value={newTicket.title}
                            onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.descriptionRequired')}</label>
                          <Textarea
                            placeholder={t('od.detailedIssuePlaceholder')}
                            value={newTicket.description}
                            onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">{t('od.priority')}</label>
                            <Select value={newTicket.priority} onValueChange={(value) => setNewTicket(prev => ({ ...prev, priority: value }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">{t('od.priorityLow')}</SelectItem>
                                <SelectItem value="medium">{t('od.priorityMedium')}</SelectItem>
                                <SelectItem value="high">{t('od.priorityHigh')}</SelectItem>
                                <SelectItem value="urgent">{t('od.priorityUrgent')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm font-medium">{t('od.category')}</label>
                            <Select value={newTicket.category} onValueChange={(value) => setNewTicket(prev => ({ ...prev, category: value }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="technical">{t('od.catTechnical')}</SelectItem>
                                <SelectItem value="billing">{t('od.catBilling')}</SelectItem>
                                <SelectItem value="product">{t('od.catProduct')}</SelectItem>
                                <SelectItem value="account">{t('od.catAccount')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setCreateTicketOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleCreateTicket}>
                            {t('od.createTicket')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={scheduleCallbackOpen} onOpenChange={setScheduleCallbackOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Target className="h-4 w-4 mr-2" />
                        {t('od.scheduleCallback')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('od.scheduleCallback')}</DialogTitle>
                        <DialogDescription>
                          {t('od.scheduleCallbackDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.customerNameRequired')}</label>
                          <Input
                            placeholder={t('od.customerNamePlaceholder')}
                            value={callbackData.customer}
                            onChange={(e) => setCallbackData(prev => ({ ...prev, customer: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.phoneNumber')}</label>
                          <Input
                            placeholder={t('od.phonePlaceholder')}
                            value={callbackData.phone}
                            onChange={(e) => setCallbackData(prev => ({ ...prev, phone: e.target.value }))}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">{t('od.dateRequired')}</label>
                            <Input
                              type="date"
                              value={callbackData.date}
                              onChange={(e) => setCallbackData(prev => ({ ...prev, date: e.target.value }))}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">{t('od.timeRequired')}</label>
                            <Input
                              type="time"
                              value={callbackData.time}
                              onChange={(e) => setCallbackData(prev => ({ ...prev, time: e.target.value }))}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.notes')}</label>
                          <Textarea
                            placeholder={t('od.callbackNotesPlaceholder')}
                            value={callbackData.notes}
                            onChange={(e) => setCallbackData(prev => ({ ...prev, notes: e.target.value }))}
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setScheduleCallbackOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleScheduleCallback}>
                            {t('od.scheduleCallback')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={generateReportOpen} onOpenChange={setGenerateReportOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <FileText className="h-4 w-4 mr-2" />
                        {t('od.generateReport')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.generateReport')}</DialogTitle>
                        <DialogDescription>
                          {t('od.generateReportDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.reportType')}</label>
                          <Select value={reportConfig.type} onValueChange={(value) => setReportConfig(prev => ({ ...prev, type: value }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="performance">{t('od.reportPerformance')}</SelectItem>
                              <SelectItem value="activity">{t('od.reportActivity')}</SelectItem>
                              <SelectItem value="customer">{t('od.reportCustomer')}</SelectItem>
                              <SelectItem value="resolution">{t('od.reportResolution')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.reportInclude')}</label>
                          <div className="space-y-1 mt-1">
                            {['transcript', 'ticket', 'callback', 'resolution'].map((k) => (
                              <label key={k} className="flex items-center space-x-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={!!reportIncludes[k]}
                                  onChange={(e) => setReportIncludes(prev => ({ ...prev, [k]: e.target.checked }))}
                                />
                                <span>{t('od.reportInclude_' + k)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.reportNotesLabel')}</label>
                          <Textarea
                            value={reportNotes}
                            onChange={(e) => setReportNotes(e.target.value)}
                            placeholder={t('od.reportNotesPlaceholder')}
                            rows={2}
                          />
                        </div>
                        {!activeInteraction && (
                          <p className="text-sm text-amber-600">{t('od.noReportContextDesc')}</p>
                        )}
                        {activeInteraction && (
                          <p className="text-xs text-gray-500">
                            {t('od.reportContextFor', { customer: activeInteraction.customer_display_name || t('od.customerFallback') })}
                          </p>
                        )}
                        {lastReport && (
                          <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-1">
                            <div className="text-sm font-medium text-green-800">{lastReport.report_title}</div>
                            <div className="text-xs text-gray-600">
                              {t('od.reportStatusLine', { status: lastReport.report_status, format: lastReport.export_format })}
                            </div>
                            <pre className="text-xs whitespace-pre-wrap max-h-40 overflow-auto bg-white/60 rounded p-2 border">{lastReport.report_url_or_content}</pre>
                            <div className="flex space-x-2 pt-1">
                              <Button size="sm" variant="outline" onClick={copyReport}>{t('od.reportCopy')}</Button>
                              <Button size="sm" variant="outline" onClick={downloadReport}>{t('od.reportDownload')}</Button>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setGenerateReportOpen(false)}>
                            {t('common.close')}
                          </Button>
                          <Button onClick={handleGenerateReport} disabled={reportBusy || !activeInteraction}>
                            {reportBusy ? t('od.reportGenerating') : t('od.generateReport')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={screenShareOpen} onOpenChange={setScreenShareOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Monitor className="h-4 w-4 mr-2" />
                        {t('od.screenShare')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.screenShare')}</DialogTitle>
                        <DialogDescription>
                          {t('od.screenShareDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        {!activeInteraction && (
                          <p className="text-sm text-amber-600">{t('od.screenShareRequiresInteractionDesc')}</p>
                        )}
                        {!screenShareSession && activeInteraction && (
                          <div>
                            <label className="text-sm font-medium">{t('od.screenShareReasonLabel')}</label>
                            <Textarea
                              value={screenShareReason}
                              onChange={(e) => setScreenShareReason(e.target.value)}
                              placeholder={t('od.screenShareReasonPlaceholder')}
                              rows={2}
                            />
                            <p className="text-xs text-gray-500 mt-2">{t('od.screenShareHonestNote')}</p>
                          </div>
                        )}
                        {screenShareSession && (
                          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-1">
                            <div className="text-sm font-medium text-blue-800">
                              {t('od.screenShareStatusLabel')}: {t('od.ssStatus_' + (screenShareSession.status || 'unknown'))}
                            </div>
                            {screenShareSession.request_message && (
                              <div className="text-xs text-gray-600">{screenShareSession.request_message}</div>
                            )}
                            <div className="text-xs text-gray-500">
                              {t('od.screenShareWith', { customer: screenShareSession.customer_display_name || t('od.customerFallback') })}
                            </div>
                            {screenShareSession.join_url
                              ? <a className="text-xs text-blue-700 underline" href={screenShareSession.join_url}>{t('od.screenShareJoin')}</a>
                              : <div className="text-xs text-gray-500">{t('od.screenShareNoLinkYet')}</div>}
                          </div>
                        )}
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setScreenShareOpen(false)}>
                            {t('common.close')}
                          </Button>
                          {screenShareSession && ['requested', 'pending_customer_acceptance', 'accepted', 'active'].includes(screenShareSession.status) && (
                            <Button variant="destructive" onClick={cancelScreenShare} disabled={screenShareBusy}>
                              {t('od.screenShareCancel')}
                            </Button>
                          )}
                          <Button
                            onClick={handleScreenShare}
                            disabled={screenShareBusy || !activeInteraction || !!screenShareSession}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {screenShareBusy ? t('od.screenShareRequesting') : t('od.screenShareRequest')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* ── Customer Communication ─────────────────────────────────────────────
                      Real, backend-gated actions on the active customer. Buttons stay visible
                      but are disabled (with an honest hint) when there is no active customer. */}
                  <div className="pt-2 border-t border-gray-200/60">
                    <p className="text-xs font-medium text-gray-500 mb-2">{t('od.customerCommunication')}</p>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleCallActiveCustomer}
                    disabled={commBusy || (!activeInteraction && !currentTicket)}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {t('od.callCustomer')}
                  </Button>

                  <Dialog open={quickMessageOpen} onOpenChange={setQuickMessageOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={handleMessageActiveCustomer}
                        disabled={commBusy || (!activeInteraction && !currentTicket)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {t('od.messageCustomer')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('od.quickMessage')}</DialogTitle>
                        <DialogDescription>{t('od.quickMessageDesc')}</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Textarea
                          placeholder={t('od.quickMessagePlaceholder')}
                          value={quickMessageText}
                          onChange={(e) => setQuickMessageText(e.target.value)}
                          rows={4}
                        />
                        <div className="flex justify-end space-x-2 pt-2">
                          <Button variant="outline" onClick={() => setQuickMessageOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleSendQuickMessage} disabled={commBusy || !quickMessageText.trim()}>
                            {commBusy ? t('od.sending') : t('od.sendMessage')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={uadEmailOpen} onOpenChange={setUadEmailOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={handleEmailActiveCustomer}
                        disabled={commBusy || (!activeInteraction && !currentTicket)}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        {t('od.emailCustomer')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('od.emailCustomer')}</DialogTitle>
                        <DialogDescription>{t('od.emailCustomerDesc')}</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.emailToLabel')}</label>
                          <Input value={uadEmailTo} disabled readOnly />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.emailSubjectLabel')}</label>
                          <Input
                            placeholder={t('od.emailSubjectPlaceholder')}
                            value={uadEmailData.subject}
                            onChange={(e) => setUadEmailData(prev => ({ ...prev, subject: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.emailBodyLabel')}</label>
                          <Textarea
                            placeholder={t('od.emailBodyPlaceholder')}
                            value={uadEmailData.body}
                            onChange={(e) => setUadEmailData(prev => ({ ...prev, body: e.target.value }))}
                            rows={5}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-2">
                          <Button variant="outline" onClick={() => setUadEmailOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleSendUadEmail} disabled={commBusy || !uadEmailData.subject || !uadEmailData.body}>
                            {commBusy ? t('od.sending') : t('od.sendEmail')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.transferCall')}</DialogTitle>
                        <DialogDescription>
                          {t('od.transferCallDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.transferTo')}</label>
                          {transferTargets.length === 0 ? (
                            <p className="text-sm text-gray-500 mt-1">{t('od.noAvailableStaff')}</p>
                          ) : (
                            <Select value={transferTarget} onValueChange={setTransferTarget}>
                              <SelectTrigger>
                                <SelectValue placeholder={t('od.selectTransferDest')} />
                              </SelectTrigger>
                              <SelectContent>
                                {transferTargets.map((s) => (
                                  <SelectItem key={s.operator_id} value={s.operator_id}>
                                    {s.display_name}{s.role ? ` · ${s.role}` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.transferNotesLabel')}</label>
                          <Textarea
                            value={transferNotes}
                            onChange={(e) => setTransferNotes(e.target.value)}
                            placeholder={t('od.transferNotesPlaceholder')}
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleTransferCall} disabled={controlBusy || !transferTarget}>
                            {t('od.transferCall')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={volumeDialogOpen} onOpenChange={setVolumeDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.adjustVolume')}</DialogTitle>
                        <DialogDescription>
                          {t('od.adjustVolumeDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">{t('od.volumeLevel', { value: volumeLevel })}</label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={volumeLevel}
                            onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>0%</span>
                            <span>50%</span>
                            <span>100%</span>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setVolumeDialogOpen(false)}>
                            {t('common.close')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Save & Resolve Dialog */}
                  <Dialog open={saveResolveDialogOpen} onOpenChange={setSaveResolveDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.saveResolveTicket')}</DialogTitle>
                        <DialogDescription>
                          {t('od.saveResolveDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.resolutionNotesLabel')}</label>
                          <Textarea
                            placeholder={t('od.resolutionPlaceholder')}
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            rows={4}
                            className="mt-1"
                          />
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <div className="flex items-center">
                            <Target className="h-4 w-4 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">{t('od.resolveInfo')}</span>
                          </div>
                        </div>

                        {/* Operator PRE-SUBMISSION Solution Bank surface (opt-in). Author/edit the REUSABLE
                            resolution; it is submitted for Manager/Admin review via the canonical create
                            endpoint (never auto-approved). Operators cannot approve. */}
                        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              aria-label="Submit resolution to Solution Bank for manager review"
                              checked={submitToSolutionBank}
                              onChange={(e) => setSubmitToSolutionBank(e.target.checked)}
                            />
                            Submit this resolution to the Solution Bank for manager review
                          </label>
                          {submitToSolutionBank && (
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs font-medium text-gray-600">Title</label>
                                <Input
                                  placeholder="Short problem title"
                                  value={solutionTitle}
                                  onChange={(e) => setSolutionTitle(e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-600">Resolution Steps (required)</label>
                                <Textarea
                                  placeholder="Describe the exact steps that resolved the issue (one per line)"
                                  value={solutionResolutionSteps}
                                  onChange={(e) => setSolutionResolutionSteps(e.target.value)}
                                  rows={4}
                                  className="mt-1"
                                />
                              </div>
                              <div className="text-xs text-gray-500">
                                Source channel: <span className="font-medium">{activeInteraction ? 'voice_call' : 'human_chat'}</span> (auto) · A manager or admin must approve before it is reused by AI.
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setSaveResolveDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            onClick={handleSaveResolve}
                            className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700"
                          >
                            {t('od.saveResolve')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Escalate Dialog */}
                  <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('od.escalateTicket')}</DialogTitle>
                        <DialogDescription>
                          {t('od.escalateTicketDesc')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('od.escalationReasonLabel')}</label>
                          <Textarea
                            placeholder={t('od.escalationPlaceholder')}
                            value={escalationReason}
                            onChange={(e) => setEscalationReason(e.target.value)}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('od.escalateTo')}</label>
                          <Select value={escalateTo} onValueChange={setEscalateTo}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manager">{t('od.destManager')}</SelectItem>
                              <SelectItem value="admin">{t('od.escAdmin')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <div className="flex items-center">
                            <Award className="h-4 w-4 text-orange-600 mr-2" />
                            <span className="text-sm text-orange-800">{t('od.escalateInfo')}</span>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setEscalateDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            onClick={handleEscalate}
                            variant="outline"
                            className="border-orange-200 text-orange-700 hover:bg-orange-50"
                          >
                            {t('od.escalateTicket')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default UnifiedAgentDesktop;
